import { items, type Slip } from './slips'

/**
 * What stays on the rail and for how long.
 *
 * The API decides what an order is; it does not decide how long a kitchen wants to keep
 * looking at one. Two things follow from that, and both are worked out here rather than
 * asked for, because the API has no field for either.
 */

/** When each order was first seen finished, so it can be retired a while later. */
export type ReadySince = Map<string, number>

/** Notes when an order became ready, and forgets ones that are no longer ready. */
export function trackReady(previous: ReadySince, slips: Slip[], now: number): ReadySince {
  const next: ReadySince = new Map()
  for (const slip of slips) {
    if (slip.readyForPickup !== true) continue
    next.set(slip.id, previous.get(slip.id) ?? now)
  }
  return next
}

/**
 * An order finished a while ago stops taking up room. Zero means never — a kitchen that
 * wants to keep seeing what it has made keeps seeing it.
 */
export function hideLongFinished(slips: Slip[], readySince: ReadySince, now: number, hideAfterSeconds: number): Slip[] {
  if (hideAfterSeconds <= 0) return slips
  return slips.filter((slip) => {
    if (slip.readyForPickup !== true) return true
    const since = readySince.get(slip.id)
    return since === undefined || now - since < hideAfterSeconds * 1000
  })
}

/**
 * Which orders have been ready long enough to count as collected. Zero means none of
 * them — somebody hands the food over and says so.
 */
export function dueForAutoPickup(slips: Slip[], readySince: ReadySince, now: number, afterSeconds: number): string[] {
  if (afterSeconds <= 0) return []
  return slips
    .filter((slip) => {
      if (slip.readyForPickup !== true) return false
      const since = readySince.get(slip.id)
      return since !== undefined && now - since >= afterSeconds * 1000
    })
    .map((slip) => slip.id)
}

/**
 * An order the API has stopped returning while the kitchen was still making it. At a
 * drive-through the guest pays at the window and the order leaves the queue the moment
 * it is paid — but the food is not made yet, so the ticket has to stay.
 *
 * goneAt is null while the order is still in the answer, and the moment it disappeared
 * once it is not.
 */
export type Tracked = { slip: Slip; goneAt: number | null }
export type Ghosts = Map<string, Tracked>

/**
 * Keeps an order that vanished before the kitchen had finished it, and forgets one that
 * was finished, cleared by hand, or has been held long enough that nobody is coming.
 */
export function updateGhosts(previous: Ghosts, slips: Slip[], now: number, keep: boolean, expireMinutes: number, cleared: ReadonlySet<string>): Ghosts {
  if (!keep) return new Map()
  const present = new Map(slips.map((slip) => [slip.id, slip] as const))
  const next: Ghosts = new Map()

  for (const [id, tracked] of previous) {
    if (cleared.has(id) || present.has(id)) continue
    const goneAt = tracked.goneAt ?? now
    // A kept ticket is a safety net for a kitchen that forgets to clear it, not a
    // permanent fixture on the rail.
    if (now - goneAt >= expireMinutes * 60_000) continue
    next.set(id, { slip: tracked.slip, goneAt })
  }
  for (const [id, slip] of present) {
    if (cleared.has(id)) continue
    // An order the kitchen has already finished left for a good reason.
    if (slip.readyForPickup === true) continue
    const lines = (slip.lineItemSections ?? []).filter((section) => section && !section.isSecondary).flatMap((section) => (section ? items(section) : []))
    if (lines.length && lines.every((line) => line.preparationCompletedAt)) continue
    next.set(id, { slip, goneAt: null })
  }
  return next
}

/** The kept orders that are no longer in the answer, to be shown alongside it. */
export function ghostSlips(ghosts: Ghosts, slips: Slip[]): Slip[] {
  const present = new Set(slips.map((slip) => slip.id))
  return [...ghosts.values()].filter((tracked) => tracked.goneAt !== null && !present.has(tracked.slip.id)).map((tracked) => tracked.slip)
}
